import { useCallback, useEffect, useRef } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { TRAFFICKING_TABLE_NAMES, TraffickingTableName } from '../../../../constants';
import type { UseFilters } from '../../../../contexts/FilterContext/hooks/useFilters';
import { getSelectedRowIdsFromState } from '../../utils';
import type { AdModel, CampaignModel, LineItemModel } from '../modelConverters';
import { getDeselectionOrigin } from './cascadingUtils';
import type { SelectedRowIds } from './types';
import type { AssociatedEntities, ParentIdsOfChildren } from './useAssociatedEntities';
import type { TraffickerTableState } from './useTraffickerTable';

type CascadingTables = {
  campaigns: TraffickerTableState<CampaignModel>;
  lineItems: TraffickerTableState<LineItemModel>;
  ads: TraffickerTableState<AdModel>;
};

export type UseCascadingDeselectionParams = {
  enabled: boolean;
  traffickerTables: CascadingTables;
  selectedRowIdsByTab: Record<TraffickingTableName, string[]>;
  applyTableFilter: UseFilters['applyTableFilter'];
  setIdsToQueryByTab: Record<TraffickingTableName, (ids: SelectedRowIds) => void>;
  getDescendantEntities: (tableName: TraffickingTableName, ids: string[]) => Promise<AssociatedEntities>;
  getAncestorEntities: (tableName: TraffickingTableName, ids: string[]) => AssociatedEntities;
  getParentIdsOfChildren: (
    childTable: TraffickingTableName,
    childIds: string[],
    options?: { cacheOnly?: boolean }
  ) => Promise<ParentIdsOfChildren>;
};

const emptyAuthoredIds = (): Record<TraffickingTableName, Set<string>> => ({
  [TraffickingTableName.campaigns]: new Set<string>(),
  [TraffickingTableName.lineItems]: new Set<string>(),
  [TraffickingTableName.ads]: new Set<string>(),
});

/**
 * Cascades a row deselection through the Campaign > LineItem > Ad hierarchy (KATANA-1714).
 *
 * Downward it is unconditional: unchecking a campaign clears every one of its line items and
 * ads, unchecking a line item clears its ads. Upward it is conditional, and only ever one level
 * at a time — a parent is cleared solely when it has no selected child left. Unchecking one ad
 * out of three leaves its line item selected; unchecking the last one clears the line item, and
 * if that was the campaign's last selected line item the campaign goes too.
 *
 * Replaces the legacy clearAssociatedEntities path when the flag is on.
 */
const useCascadingDeselection = ({
  enabled,
  traffickerTables,
  selectedRowIdsByTab,
  applyTableFilter,
  setIdsToQueryByTab,
  getDescendantEntities,
  getAncestorEntities,
  getParentIdsOfChildren,
}: UseCascadingDeselectionParams): void => {
  // Ids this hook has removed itself, per tab, plus the origin ids of a cascade in flight.
  // Subtracted from the observed diff so our own removals never read as a fresh user
  // deselection on a later run. Mirror of the ref in useCascadingSelection, with the pruning
  // condition inverted: a removal is steady state once the filter context has stopped listing it.
  const deselectAuthoredIdsRef = useRef<Record<TraffickingTableName, Set<string>>>(emptyAuthoredIds());

  // The cascade outlives the effect run that started it — useTraffickerTable pushes the click
  // into the filter context in the same commit, so this effect is always re-run, and previously
  // torn down, while the descendant fetch is in flight. Only unmount may abandon the writes.
  const isMountedRef = useRef(true);

  useEffect(
    () => (): void => {
      isMountedRef.current = false;
    },
    []
  );

  /** Removes ids from a tab and returns the ones that were actually selected to begin with. */
  const removeSelections = useCallback(
    (tableName: TraffickingTableName, ids: string[]): string[] => {
      if (!ids.length) return [];

      const { tableInstance } = traffickerTables[tableName];
      // Read live react-table state rather than the filter context copy, which lags a render
      // behind and would resurrect rows a concurrent cascade has already removed.
      const existingSelectedRowIds = tableInstance.state.selectedRowIds;
      const idsToRemove = ids.filter((id) => existingSelectedRowIds[id]);

      if (!idsToRemove.length) return [];

      const removed = new Set(idsToRemove);
      const remaining = Object.keys(existingSelectedRowIds).filter((id) => !removed.has(id));

      idsToRemove.forEach((id) => deselectAuthoredIdsRef.current[tableName].add(id));

      // Table state before the filter context — see the ordering note on
      // unselectAssociatedEntities in useTraffickerState. Writing the context first would let a
      // synchronous flush show these rows as freshly added and re-cascade them back in.
      tableInstance.state.selectedRowIds = getSelectedRowIdsFromState(remaining);
      applyTableFilter(tableName, 'selectedRowIds', remaining);
      setIdsToQueryByTab[tableName](getSelectedRowIdsFromState(remaining));

      return idsToRemove;
    },
    [applyTableFilter, setIdsToQueryByTab, traffickerTables]
  );

  /**
   * Of the given parents, the ones with no selected child left.
   *
   * childTable's selection is read live, so callers must apply their downward removals first.
   */
  const getOrphanedParentIds = useCallback(
    async (parentIds: string[], childTable: TraffickingTableName): Promise<string[]> => {
      if (!parentIds.length) return [];

      const selectedChildIds = Object.keys(traffickerTables[childTable].tableInstance.state.selectedRowIds);
      const cached = await getParentIdsOfChildren(childTable, selectedChildIds, { cacheOnly: true });
      const stillUnaccounted = parentIds.filter((parentId) => !cached.parentIds.has(parentId));

      // Every candidate already has a selected child in the cache, so nothing here is orphaned.
      if (!stillUnaccounted.length) return [];

      const fetched = await getParentIdsOfChildren(childTable, cached.unresolvedChildIds);

      // An unattributed child may belong to one of these parents, so orphan nothing this pass.
      if (fetched.unresolvedChildIds.length) return [];

      return stillUnaccounted.filter((parentId) => !fetched.parentIds.has(parentId));
    },
    [getParentIdsOfChildren, traffickerTables]
  );

  useEffect(() => {
    if (!enabled) return;

    const scopes = {
      [TraffickingTableName.campaigns]: {
        previous: selectedRowIdsByTab.campaigns,
        current: Object.keys(traffickerTables.campaigns.tableInstance.state.selectedRowIds),
      },
      [TraffickingTableName.lineItems]: {
        previous: selectedRowIdsByTab.lineItems,
        current: Object.keys(traffickerTables.lineItems.tableInstance.state.selectedRowIds),
      },
      [TraffickingTableName.ads]: {
        previous: selectedRowIdsByTab.ads,
        current: Object.keys(traffickerTables.ads.tableInstance.state.selectedRowIds),
      },
    };

    const origin = getDeselectionOrigin(scopes);

    // Drop authored ids the filter context has caught up on. From then on they are steady state,
    // so unchecking the same row again is genuine user intent.
    TRAFFICKING_TABLE_NAMES.forEach((tableName) => {
      const authoredIds = deselectAuthoredIdsRef.current[tableName];
      const stillInFilters = new Set(scopes[tableName].previous);

      authoredIds.forEach((id) => {
        if (!stillInFilters.has(id)) authoredIds.delete(id);
      });
    });

    if (!origin) return;

    const userRemovedIds = origin.removedIds.filter((id) => !deselectAuthoredIdsRef.current[origin.tableName].has(id));

    if (!userRemovedIds.length) return;

    // Claim the origin ids before awaiting, so the guaranteed re-run cannot start a second
    // cascade for the same click while the first one is still resolving.
    userRemovedIds.forEach((id) => deselectAuthoredIdsRef.current[origin.tableName].add(id));

    const cascade = async (): Promise<void> => {
      // Downward first, and unconditionally. Ads origin resolves to no descendants and issues
      // no query at all.
      const descendants = await getDescendantEntities(origin.tableName, userRemovedIds);

      if (!isMountedRef.current) return;

      // One commit for the pair, so it costs one ads refetch rather than two.
      unstable_batchedUpdates(() => {
        removeSelections(TraffickingTableName.lineItems, descendants.lineItems ?? []);
        removeSelections(TraffickingTableName.ads, descendants.ads ?? []);
      });

      // Then upward, one level at a time. Which line items just lost their selection decides
      // whether any campaign is now childless.
      let deselectedLineItemIds: string[] = [];

      if (origin.tableName === TraffickingTableName.lineItems) {
        deselectedLineItemIds = userRemovedIds;
      }

      if (origin.tableName === TraffickingTableName.ads) {
        const { lineItems: parentLineItemIds = [] } = getAncestorEntities(TraffickingTableName.ads, userRemovedIds);
        const orphanedLineItemIds = await getOrphanedParentIds(parentLineItemIds, TraffickingTableName.ads);

        if (!isMountedRef.current) return;

        deselectedLineItemIds = removeSelections(TraffickingTableName.lineItems, orphanedLineItemIds);
      }

      if (!deselectedLineItemIds.length) return;

      // From the cache, so a line item that was never paged into its own tab still resolves.
      const { campaigns: parentCampaignIds = [] } = getAncestorEntities(
        TraffickingTableName.lineItems,
        deselectedLineItemIds
      );
      const orphanedCampaignIds = await getOrphanedParentIds(parentCampaignIds, TraffickingTableName.lineItems);

      if (!isMountedRef.current) return;

      removeSelections(TraffickingTableName.campaigns, orphanedCampaignIds);
    };

    // Writes are interleaved with the awaits, so a failed query can leave the hierarchy partly
    // cleared and there is nothing to roll back. Report and stop.
    cascade().catch((error) => {
      console.error('Cascading deselection failed; selection may be partly cleared.', error);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enabled,
    removeSelections,
    getOrphanedParentIds,
    getDescendantEntities,
    getAncestorEntities,
    selectedRowIdsByTab.campaigns,
    selectedRowIdsByTab.lineItems,
    selectedRowIdsByTab.ads,
    traffickerTables.campaigns.tableInstance.state.selectedRowIds,
    traffickerTables.lineItems.tableInstance.state.selectedRowIds,
    traffickerTables.ads.tableInstance.state.selectedRowIds,
  ]);
};

export default useCascadingDeselection;
